/**
 * Client API for per-account scheduled runs.
 * The server reads the subdomain from the JWT — the client never sends it.
 *
 * Types are inferred from the shared zod schemas so the client and server
 * agree on the wire shape by construction.
 */
import { apiFetch } from './api';
import type {
  CreateTaskInput,
  PatchTaskInput,
  Schedule,
  ScheduledRunTask,
} from '@/shared/scheduled-runs';

export type { CreateTaskInput, PatchTaskInput, Schedule, ScheduledRunTask };

const BASE = '/api/scheduled-runs';

export async function listScheduledRuns(): Promise<ScheduledRunTask[]> {
  const { tasks } = await apiFetch<{ tasks: ScheduledRunTask[] }>(BASE);
  return tasks;
}

export async function getScheduledRun(name: string): Promise<ScheduledRunTask> {
  const { task } = await apiFetch<{ task: ScheduledRunTask }>(`${BASE}/${encodeURIComponent(name)}`);
  return task;
}

export async function createScheduledRun(input: CreateTaskInput): Promise<ScheduledRunTask> {
  const { task } = await apiFetch<{ task: ScheduledRunTask }>(BASE, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return task;
}

export async function patchScheduledRun(
  name: string,
  patch: PatchTaskInput,
): Promise<ScheduledRunTask> {
  const { task } = await apiFetch<{ task: ScheduledRunTask }>(
    `${BASE}/${encodeURIComponent(name)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    },
  );
  return task;
}

export async function deleteScheduledRun(name: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`${BASE}/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}
